import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  onSnapshot,
  arrayUnion,
  updateDoc,
  getDoc,
  orderBy,
} from 'firebase/firestore';
import { isEmpty } from 'lodash';
import { MESSAGE_TYPE } from '../constants';
import { db } from './firebase';

export const createConversation = async (participants, careCircleCRId, name) => {
  try {
    const conversationsRef = collection(db, 'conversations');

    const conversationsQuery = query(conversationsRef, where('participants', '==', participants.sort()));

    const querySnapshot = await getDocs(conversationsQuery);

    if (!querySnapshot.empty) {
      return { success: false, message: 'conversation already exists!' };
    }

    const unreadCounts = {};

    for (let i = 0; i < participants.length; i++) {
      unreadCounts[participants[i]] = 0;
    }

    const docRef = await addDoc(conversationsRef, {
      participants: participants.sort(),
      createdAt: new Date(),
      updatedAt: new Date(),
      careCircleCRId,
      name,
      lastMessage: {
        text: '',
        createdAt: new Date(),
      },
      unreadCounts,
    });
    return { success: true, message: docRef.id };
  } catch (error) {
    console.error('Error creating conversation: ', error);
  }
};

export const sendMessage = async (conversationId, senderId, text) => {
  try {
    const messagesRef = collection(db, `conversations/${conversationId}/messages`);
    const conversationRef = doc(db, `conversations`, conversationId);

    await addDoc(messagesRef, {
      senderId,
      text,
      createdAt: new Date(),
      readBy: [],
      type: MESSAGE_TYPE.text,
      attachments: [
        {
          type: '',
          url: '',
          metaData: {},
        },
      ],
    });

    const conversationSnapshot = await getDoc(conversationRef);
    if (conversationSnapshot.exists()) {
      const conversationData = conversationSnapshot.data();
      const updates = {};

      conversationData.participants.forEach((participant) => {
        if (participant !== senderId) {
          updates[`unreadCounts.${participant}`] = (conversationData.unreadCounts?.[participant] || 0) + 1;
          updates['lastMessage'] = {
            text,
            createdAt: new Date(),
          };
        }
      });

      await updateDoc(conversationRef, updates);

      console.log('Message added and unread counts updated.');
    }
  } catch (error) {
    console.error('Error adding message and updating unread counts: ', error);
  }
};

export const getConversations = (userId, careCircleCRId, callback) => {
  try {
    const recipientId = !isEmpty(careCircleCRId) ? careCircleCRId : userId;
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      where('careCircleCRId', '==', recipientId),
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const conversations = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      callback(conversations);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error subscribing to conversations: ', error);
  }
};

export const subscribeToMessages = (conversationId, callback) => {
  const messagesRef = collection(db, `conversations/${conversationId}/messages`);
  const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
  return onSnapshot(messagesQuery, callback);
};

export const markMessageAsRead = async (conversationId, messageId, userId) => {
  try {
    const messageRef = doc(db, `conversations/${conversationId}/messages`, messageId);
    const conversationRef = doc(db, `conversations`, conversationId);

    // Fetch the message to check if it's already read by the user
    const messageSnapshot = await getDoc(messageRef);
    if (messageSnapshot.exists()) {
      const messageData = messageSnapshot.data();

      // Check if the user is not the sender and hasn't already read the message
      if (messageData.senderId !== userId && !messageData.readBy?.includes(userId)) {
        // Update the message's readBy array
        await updateDoc(messageRef, {
          readBy: arrayUnion(userId),
        });

        // Decrement the user's unread count in the conversation
        const conversationSnapshot = await getDoc(conversationRef);
        if (conversationSnapshot.exists()) {
          const conversationData = conversationSnapshot.data();
          if (conversationData.unreadCounts?.[userId] > 0) {
            await updateDoc(conversationRef, {
              [`unreadCounts.${userId}`]: 0,
            });
          }

          console.log(`Message ${messageId} marked as read by user ${userId}, unread count updated.`);
        }
      } else {
        console.log(`Message ${messageId} not updated. Either the user is the sender or has already read it.`);
      }
    } else {
      console.log(`Message ${messageId} not found.`);
    }
  } catch (error) {
    console.error('Error marking message as read and updating unread count: ', error);
  }
};

export const sendCompanionMessage = async (conversationId, senderId, text) => {
  try {
    const messagesRef = collection(db, `companionConversations/${conversationId}/messages`);
    const conversationRef = doc(db, `conversations`, conversationId);

    await addDoc(messagesRef, {
      senderId,
      text,
      createdAt: new Date(),
      readBy: [],
      type: MESSAGE_TYPE.text,
      attachments: [
        {
          type: '',
          url: '',
          metaData: {},
        },
      ],
    });

    const conversationSnapshot = await getDoc(conversationRef);
    if (conversationSnapshot.exists()) {
      const conversationData = conversationSnapshot.data();
      const updates = {};

      conversationData.participants.forEach((participant) => {
        if (participant !== senderId) {
          updates[`unreadCounts.${participant}`] = (conversationData.unreadCounts?.[participant] || 0) + 1;
          updates['lastMessage'] = {
            text,
            createdAt: new Date(),
          };
        }
      });

      await updateDoc(conversationRef, updates);

      console.log('Message added and unread counts updated.');
    }
  } catch (error) {
    console.error('Error adding message and updating unread counts: ', error);
  }
};

export const subscribeToCompanionMessage = (conversationId, callback) => {
  const messagesRef = collection(db, `companionConversations/${conversationId}/messages`);
  const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
  return onSnapshot(messagesQuery, callback);
};
